import { parseISO } from "date-fns";
import { usePostHog } from "posthog-js/react";
import type React from "react";

import type { AppTicketDto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { User } from "@/components/User/User";
import { Badge } from "@/design-system/Badge";
import { Card } from "@/design-system/Card";
import { Link } from "@/design-system/Link";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { getVisibilityOptionIcon } from "../../list/helpers";

interface ResidentTicketCardProps {
  ticket: AppTicketDto;
  showRating?: boolean;
}

export function ResidentTicketCard({ ticket, showRating }: ResidentTicketCardProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const slug = useSlug();
  const postHog = usePostHog();

  return (
    <Link
      key={ticket.id}
      href={routes.tickets.details({
        slug,
        id: ticket.id,
      })}
      tone="default"
      underline="none"
      fullWidth
      onClick={() => postHog.capture("clicked_ticket")}
    >
      <Card interactive>
        <Stack gap="sm">
          <Spread space="sm">
            <TicketStatus labelColor={ticket.status.color} description={ticket.status.name} variant="filled" />
            {ticket.visibility !== "private" && (
              <Badge icon={getVisibilityOptionIcon(ticket.visibility)} size="md" variant="info">
                {t("page.tickets.details.info.type.collective")}
              </Badge>
            )}
          </Spread>

          {/* Header */}
          <User
            user={ticket.user}
            renderSubtitle={() => formatDate(i18n, "datetime", parseISO(ticket.postedAt))}
            shouldNavigate={false}
          />

          <Spread space="sm" align="start">
            <span className="flex items-center gap-space-xs text-body-emphasis">{ticket.title}</span>
          </Spread>
        </Stack>
        {showRating && !!ticket.rating && <RatingBadge rating={ticket.rating} />}
      </Card>
    </Link>
  );
}
